<template>
  <k-form-section :column-span="1" legend="Time and Date" class="set-meeting-datetime">
    <div class="time-and-date-container">
      <k-form-field label="Date" :required="true" field-id="date">
        <k-date-picker
          :model-value="modelValue.meetingDate"
          input-id="date"
          :modelConfig="{ timeAdjust: '00:00:00' }"
          :popover="{ visibility: 'focus' }"
          @update:modelValue="onDateChange"
        />
      </k-form-field>
      <k-form-field label="Start Time" :required="true" field-id="start-time">
        <k-date-picker
          :model-value="modelValue.startTime"
          type="time"
          input-id="start-time"
          :time-rules="{ minutes: { interval: 15 } }"
          :modelConfig="{ timeAdjust: '00:00:00' }"
          :popover="{ visibility: 'focus' }"
          @update:modelValue="onStartTimeChange"
        />
      </k-form-field>
      <k-form-field
        label="End Time"
        :required="true"
        field-id="end-time"
        :has-warning="showStartAfterEndWarning"
      >
        <k-date-picker
          :model-value="modelValue.endTime"
          input-id="end-time"
          type="time"
          :time-rules="{ minutes: { interval: 15 } }"
          :modelConfig="{ timeAdjust: '00:00:00' }"
          :popover="{ visibility: 'focus' }"
          @update:modelValue="onEndTimeUpdate"
          @change="onEndTimeChange"
        />
      </k-form-field>
      <k-form-field label="Quick Duration" :required="false" field-id="duration">
        <k-dropdown
          id="duration"
          :modelValue="duration"
          :options="durationOptions"
          :show-search="false"
          placeholder="Use a preset"
          :class="['duration-dropdown', { 'dropdown-upward': dropdownUpward }]"
          @update:modelValue="onDurationChange"
        />
      </k-form-field>
    </div>
    <p
      v-if="showStartAfterEndWarning"
      class="date-warning-message helper-text"
    >
      Meeting start time cannot be later than the end time.
    </p>
  </k-form-section>
</template>

<style scoped>
.time-and-date-container {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 15px;
}

p.date-warning-message {
  text-align: right;
}

.helper-text {
  font-size: 0.9em;
  color: var(--kate-type-secondary);
  margin: 0;
  font-style: italic;
}

.duration-dropdown {
  min-width: 120px;
}

.duration-dropdown :deep(.k-search-dropdown-options) {
  max-height: 20vh;
}

.duration-dropdown :deep(.btn-outlined) {
  padding: 9px 12px;
}

.dropdown-upward :deep(.k-search-dropdown-adv-content) {
  bottom: 100%;
  top: auto;
  margin-bottom: 5px;
}
</style>

<script>
import TimeMixin from '@mixins/time-mixins';
import KDatePicker from '@base-components/k-date-picker.vue';
import { KFormSection, KFormField } from '@base-components/forms';
import KDropdown from '@base-components/k-dropdown.vue';

const DURATION_OPTIONS = [
  { id: 15, name: '15m' },
  { id: 30, name: '30m' },
  { id: 45, name: '45m' },
  { id: 60, name: '1h' },
];
const DEFAULT_DURATION_MINUTES = 60;

export default {
  name: 'set-meeting-datetime',

  components: {
    KDatePicker,
    KFormSection,
    KFormField,
    KDropdown,
  },

  mixins: [TimeMixin],

  props: {
    modelValue: {
      type: Object,
      default: () => ({
        meetingDate: null,
        startTime: null,
        endTime: null,
      }),
    },
    dropdownUpward: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:modelValue', 'validation'],

  data() {
    return {
      durationOptions: DURATION_OPTIONS,
      duration: null,
      isEndTimeManual: false,
      lastEmitted: null,
    };
  },

  computed: {
    meetingStartDate() {
      if (!this.modelValue.meetingDate || !this.modelValue.startTime) {
        return null;
      }
      return this.overrideDateComponent(this.modelValue.startTime, this.modelValue.meetingDate);
    },
    meetingEndDate() {
      if (!this.modelValue.meetingDate || !this.modelValue.endTime) {
        return null;
      }
      return this.overrideDateComponent(this.modelValue.endTime, this.modelValue.meetingDate);
    },
    showStartAfterEndWarning() {
      return Boolean(
        this.meetingStartDate
        && this.meetingEndDate
        && this.meetingStartDate > this.meetingEndDate,
      );
    },
    isValid() {
      return Boolean(
        this.meetingStartDate
        && this.meetingEndDate
        && this.meetingStartDate <= this.meetingEndDate,
      );
    },
    validationPayload() {
      return {
        isValid: this.isValid,
        meetingStartDate: this.meetingStartDate,
        meetingEndDate: this.meetingEndDate,
      };
    },
  },

  watch: {
    modelValue: {
      handler(schedule) {
        if (this.schedulesEqual(schedule, this.lastEmitted)) {
          return;
        }
        this.applyIncomingSchedule(schedule);
      },
      immediate: true,
      deep: true,
    },
    validationPayload: {
      handler(payload) {
        this.$emit('validation', payload);
      },
      immediate: true,
      deep: true,
    },
  },

  methods: {
    schedulesEqual(incoming, lastEmitted) {
      if (!incoming || !lastEmitted) {
        return false;
      }
      return this.msSinceEpoch(incoming.meetingDate) === this.msSinceEpoch(lastEmitted.meetingDate)
        && this.msSinceEpoch(incoming.startTime) === this.msSinceEpoch(lastEmitted.startTime)
        && this.msSinceEpoch(incoming.endTime) === this.msSinceEpoch(lastEmitted.endTime);
    },

    applyIncomingSchedule(schedule) {
      const meetingDate = schedule?.meetingDate || null;
      const startTime = schedule?.startTime || null;
      const endTime = schedule?.endTime || null;

      if (startTime && !endTime) {
        this.duration = DEFAULT_DURATION_MINUTES;
        this.isEndTimeManual = false;
        this.emitSchedule({
          meetingDate,
          startTime,
          endTime: new Date(startTime.getTime() + DEFAULT_DURATION_MINUTES * 60 * 1000),
        });
        return;
      }

      if (startTime && endTime) {
        const existingDuration = Math.round((endTime.getTime() - startTime.getTime()) / (60 * 1000));
        const presetDurations = this.durationOptions.map(opt => opt.id);
        this.duration = presetDurations.includes(existingDuration) ? existingDuration : null;
        this.isEndTimeManual = false;
        this.lastEmitted = { meetingDate, startTime, endTime };
      }
    },

    emitSchedule(overrides) {
      const next = {
        meetingDate: this.modelValue.meetingDate,
        startTime: this.modelValue.startTime,
        endTime: this.modelValue.endTime,
        ...overrides,
      };
      this.lastEmitted = next;
      this.$emit('update:modelValue', next);
    },

    onDateChange(meetingDate) {
      this.emitSchedule({ meetingDate });
    },

    onStartTimeChange(startTime) {
      const updates = { startTime };
      if (startTime && !this.isEndTimeManual && this.duration) {
        updates.endTime = new Date(startTime.getTime() + this.duration * 60 * 1000);
      }
      this.emitSchedule(updates);
    },

    onEndTimeUpdate(endTime) {
      this.emitSchedule({ endTime });
    },

    onEndTimeChange() {
      this.isEndTimeManual = true;
      this.duration = null;
    },

    onDurationChange(newDuration) {
      this.duration = newDuration;
      if (newDuration && this.modelValue.startTime) {
        this.isEndTimeManual = false;
        this.emitSchedule({
          endTime: new Date(this.modelValue.startTime.getTime() + newDuration * 60 * 1000),
        });
      }
    },
  },
};
</script>
